/**
 * SmartSchedulingSuggestions Component
 * AI-powered visit scheduling suggestions based on patient acuity and staff availability
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import {
  Calendar, Clock, User, AlertTriangle, TrendingUp,
  Zap, CheckCircle2, ChevronRight, Loader2, RefreshCw,
  Activity, Heart, CalendarCheck, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SmartSchedulingSuggestions = ({ onScheduleVisit, className = "" }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('hospice_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/scheduling/smart-suggest`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ days_ahead: 7, include_all_staff: false })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
        setSummary(data.summary || {});
        setTips(data.optimization_tips || []);
      } else {
        console.error('Failed to fetch suggestions');
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const quickSchedule = async (suggestion, dateSlot, timeSlot) => {
    const key = `${suggestion.patient_id}-${dateSlot.date}-${timeSlot}`;
    setScheduling(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await fetch(`${API_URL}/api/scheduling/quick-schedule`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          patient_id: suggestion.patient_id,
          date: dateSlot.date,
          start_time: timeSlot,
          visit_type: suggestion.recommended_visit_type,
          duration_minutes: 30,
          notes: `Smart scheduled: ${suggestion.reason}`
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Visit scheduled successfully!');
        
        // Remove scheduled patient from suggestions
        setSuggestions(prev => prev.filter(s => s.patient_id !== suggestion.patient_id));
        
        // Update summary
        setSummary(prev => ({
          ...prev,
          total_suggestions: (prev?.total_suggestions || 1) - 1,
          [suggestion.acuity_level + '_acuity']: Math.max(0, (prev?.[suggestion.acuity_level + '_acuity'] || 1) - 1)
        }));
        
        onScheduleVisit?.(result.visit);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to schedule visit');
      }
    } catch (error) {
      toast.error('Error scheduling visit');
      console.error(error);
    } finally {
      setScheduling(prev => ({ ...prev, [key]: false }));
    }
  };

  const getAcuityColor = (level) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getAcuityIcon = (level) => {
    switch (level) {
      case 'high': return <AlertTriangle className="w-4 h-4" />;
      case 'medium': return <Activity className="w-4 h-4" />;
      case 'low': return <Heart className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <Card className={className} data-testid="smart-scheduling-loading">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span className="text-slate-600">Analyzing patient acuity...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${className} border-2 border-indigo-200/50`} data-testid="smart-scheduling-suggestions">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Sparkles className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Smart Visit Suggestions</CardTitle>
              <CardDescription className="text-xs">
                AI-powered scheduling based on patient acuity
              </CardDescription>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSuggestions}
            className="gap-2"
            data-testid="refresh-suggestions-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="p-2 bg-red-50 rounded-lg text-center border border-red-100">
              <p className="text-lg font-bold text-red-600">{summary.high_acuity || 0}</p>
              <p className="text-xs text-red-600">High</p>
            </div>
            <div className="p-2 bg-amber-50 rounded-lg text-center border border-amber-100">
              <p className="text-lg font-bold text-amber-600">{summary.medium_acuity || 0}</p>
              <p className="text-xs text-amber-600">Medium</p>
            </div>
            <div className="p-2 bg-emerald-50 rounded-lg text-center border border-emerald-100">
              <p className="text-lg font-bold text-emerald-600">{summary.low_acuity || 0}</p>
              <p className="text-xs text-emerald-600">Low</p>
            </div>
            <div className="p-2 bg-indigo-50 rounded-lg text-center border border-indigo-100">
              <p className="text-lg font-bold text-indigo-600">{summary.total_suggestions || 0}</p>
              <p className="text-xs text-indigo-600">Total</p>
            </div>
          </div>
        )}
        
        {/* Optimization Tips */}
        {tips.length > 0 && (
          <div className="mb-4 space-y-1">
            {tips.map((tip, idx) => (
              <div key={idx} className="text-xs p-2 bg-blue-50 rounded-lg text-blue-700 border border-blue-100">
                {tip}
              </div>
            ))}
          </div>
        )}
        
        {/* Suggestions List */}
        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
          {suggestions.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
              <p className="font-medium">All caught up!</p>
              <p className="text-xs">No patients require immediate scheduling</p>
            </div>
          ) : (
            suggestions.map((suggestion, idx) => (
              <div
                key={suggestion.patient_id || idx}
                className="p-3 rounded-lg border bg-white hover:shadow-md transition-shadow"
                data-testid={`suggestion-${idx}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={`${getAcuityColor(suggestion.acuity_level)} gap-1`}>
                      {getAcuityIcon(suggestion.acuity_level)}
                      {suggestion.acuity_level?.toUpperCase()}
                    </Badge>
                    <span className="font-medium text-sm truncate">{suggestion.patient_name}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <TrendingUp className="w-3 h-3" />
                    <span>{suggestion.acuity_score}</span>
                  </div>
                </div>
                
                {/* Acuity Progress Bar */}
                <div className="mb-2">
                  <Progress 
                    value={suggestion.acuity_score} 
                    className="h-1.5"
                  />
                </div>
                
                {/* Reason */}
                <p className="text-xs text-slate-600 mb-2">
                  {suggestion.reason}
                </p>
                
                {/* PPS and Days Info */}
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                  {suggestion.pps_score && (
                    <span className="flex items-center gap-1">
                      <Activity className="w-3 h-3" />
                      PPS: {suggestion.pps_score}%
                    </span>
                  )}
                  {suggestion.days_since_visit && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {suggestion.days_since_visit}d ago
                    </span>
                  )}
                </div>
                
                {/* Suggested Dates & Quick Schedule */}
                {suggestion.suggested_dates?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-slate-600 flex items-center gap-1">
                      <CalendarCheck className="w-3 h-3" />
                      Quick Schedule:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {suggestion.suggested_dates.slice(0, 2).map((dateSlot, dIdx) => (
                        dateSlot.slots?.slice(0, 2).map((time, tIdx) => {
                          const key = `${suggestion.patient_id}-${dateSlot.date}-${time}`;
                          return (
                            <Button
                              key={`${dIdx}-${tIdx}`}
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs gap-1 hover:bg-indigo-50 hover:border-indigo-300"
                              onClick={() => quickSchedule(suggestion, dateSlot, time)}
                              disabled={scheduling[key]}
                              data-testid={`schedule-${suggestion.patient_id}-${dateSlot.date}-${time}`}
                            >
                              {scheduling[key] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <Clock className="w-3 h-3" />
                                  {dateSlot.day?.slice(0, 3)} {time}
                                </>
                              )}
                            </Button>
                          );
                        })
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SmartSchedulingSuggestions;
